import {useState, useRef} from "react";
import {Navigation} from "@/components/Navigation";
import {Footer} from "@/components/Footer";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow,} from "@/components/ui/table";
import {Button} from "@/components/ui/button";
import {Award, Crown, Medal, Trophy, AlertCircle, TrendingUp, Users, CreditCard, Coins, ChevronLeft, ChevronRight} from "lucide-react";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {useQuery} from "@tanstack/react-query";
import {ScrollArea} from "@/components/ui/scroll-area";
import {SEO} from "@/components/SEO.tsx";
import * as React from "react";

interface LeaderBoard {
  userId: string;
  totalWinnings: number;
  totalPolls?: number;
  winRate?: number;
  username: string;
}
type TimeFrame = "daily" | "weekly" | "monthly" | "all";

const Leaderboard = () => {
  const [timeFrame, setTimeFrame] = useState<TimeFrame>("all");
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const formatNumber = (num: number) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -300,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 300,
        behavior: 'smooth'
      });
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Trophy className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Medal className="w-6 h-6 text-amber-700" />;
      default:
        return <Award className="w-6 h-6 text-blue-500" />;
    }
  };

  const getTimeFrameLabel = (timeFrame: TimeFrame) => {
    switch (timeFrame) {
      case "daily":
        return "Today's";
      case "weekly":
        return "This Week's";
      case "monthly":
        return "This Month's";
      case "all":
        return "All-Time";
    }
  };

  const getEmptyStateMessage = (timeFrame: TimeFrame) => {
    switch (timeFrame) {
      case "daily":
        return "Today's heroes list coming soon";
      case "weekly":
        return "This week's heroes list coming soon";
      case "monthly":
        return "This month's heroes list coming soon";
      case "all":
        return "All-time heroes list coming soon";
    }
  };

  const {data: categoryLeaderboard, isLoading: isCategoryLeaderboardLoading} = useQuery({
    queryKey: ['categoryLeaderboard', timeFrame],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/leaderboard?range=${timeFrame}`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      return await response.json();
    }
  });

  return (
    <div className="min-h-screen bg-background">
      <SEO
          title="Leaderboard"
          description="See the top users and clans based on poll performance and points"
          type="website"
          name="blankpoll"
          imageUrl="/blankpoll-banner.jpg"
      />
      <Navigation />

      <div className="container mx-auto pt-24 px-4 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4 text-secondary dark:text-white">Leaderboard</h1>
            <p className="text-gray-600 dark:text-gray-300">
              Top heroes across different time periods
            </p>
          </div>

          <div className="relative mb-8">
            <div className="md:hidden relative">
              <div className="flex items-center">
                <Button
                  variant="outline"
                  size="icon"
                  className="flex-shrink-0 rounded-full bg-background border-gray-200 shadow-md z-10 mr-2"
                  onClick={scrollLeft}
                >
                  <ChevronLeft className="h-4 w-4 text-gray-500" />
                </Button>

                <div
                  ref={scrollContainerRef}
                  className="flex gap-4 overflow-x-auto pb-4 scrollbar-none"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  <Card className="bg-background min-w-[250px] flex-shrink-0">
                    <CardHeader className="pb-2 flex flex-col items-center">
                      <div className="rounded-full bg-primary/10 dark:bg-gray-700 w-12 h-12 flex items-center justify-center mb-2">
                        <Coins className="w-6 h-6 text-gray-500 dark:text-gray-300" />
                      </div>
                      <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Total Prize Pool Points
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-center">
                      <div className="text-2xl font-bold text-secondary dark:text-white">{categoryLeaderboard?.metrics?.totalPrize ? `${formatNumber(categoryLeaderboard?.metrics?.totalPrize)} bp` : `-`}</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-background min-w-[250px] flex-shrink-0">
                    <CardHeader className="pb-2 flex flex-col items-center">
                      <div className="rounded-full bg-primary/10 dark:bg-gray-700 w-12 h-12 flex items-center justify-center mb-2">
                        <Users className="w-6 h-6 text-gray-500 dark:text-gray-300" />
                      </div>
                      <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Total Heroes
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-center">
                      <div className="text-2xl font-bold text-secondary dark:text-white">11,898+</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-background min-w-[250px] flex-shrink-0">
                    <CardHeader className="pb-2 flex flex-col items-center">
                      <div className="rounded-full bg-primary/10 dark:bg-gray-700 w-12 h-12 flex items-center justify-center mb-2">
                        <TrendingUp className="w-6 h-6 text-gray-500 dark:text-gray-300" />
                      </div>
                      <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Average Win Amount
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-center">
                      <div className="text-2xl font-bold text-secondary dark:text-white">{categoryLeaderboard?.metrics?.averagePrize ? `${formatNumber(Math.floor(categoryLeaderboard?.metrics?.averagePrize))} bp` : `-`}</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-background min-w-[250px] flex-shrink-0">
                    <CardHeader className="pb-2 flex flex-col items-center">
                      <div className="rounded-full bg-primary/10 dark:bg-gray-700 w-12 h-12 flex items-center justify-center mb-2">
                        <CreditCard className="w-6 h-6 text-gray-500 dark:text-gray-300" />
                      </div>
                      <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Largest Win
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-center">
                      <div className="text-2xl font-bold text-secondary dark:text-white">{categoryLeaderboard?.metrics?.maximumPrize ? `${formatNumber(categoryLeaderboard?.metrics?.maximumPrize)} bp` : `-`}</div>
                    </CardContent>
                  </Card>
                </div>

                <Button
                  variant="outline"
                  size="icon"
                  className="flex-shrink-0 rounded-full bg-background border-gray-200 shadow-md z-10 ml-2"
                  onClick={scrollRight}
                >
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                </Button>
              </div>
            </div>

            <div className="hidden md:grid md:grid-cols-4 md:gap-4">
              <Card className="bg-background">
                <CardHeader className="pb-2 flex flex-col items-center">
                  <div className="rounded-full bg-primary/10 dark:bg-gray-700 w-12 h-12 flex items-center justify-center mb-2">
                    <Coins className="w-6 h-6 text-gray-500 dark:text-gray-300" />
                  </div>
                  <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Total Prize Pool Points
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="text-2xl font-bold text-secondary dark:text-white">{categoryLeaderboard?.metrics?.totalPrize ? `${formatNumber(categoryLeaderboard?.metrics?.totalPrize)} bp` : `-`}</div>
                </CardContent>
              </Card>
              <Card className="bg-background">
                <CardHeader className="pb-2 flex flex-col items-center">
                  <div className="rounded-full bg-primary/10 dark:bg-gray-700 w-12 h-12 flex items-center justify-center mb-2">
                    <Users className="w-6 h-6 text-gray-500 dark:text-gray-300" />
                  </div>
                  <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Total Heroes
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="text-2xl font-bold text-secondary dark:text-white">11,898+</div>
                </CardContent>
              </Card>
              <Card className="bg-background">
                <CardHeader className="pb-2 flex flex-col items-center">
                  <div className="rounded-full bg-primary/10 dark:bg-gray-700 w-12 h-12 flex items-center justify-center mb-2">
                    <TrendingUp className="w-6 h-6 text-gray-500 dark:text-gray-300" />
                  </div>
                  <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Average Win Amount
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="text-2xl font-bold text-secondary dark:text-white">{categoryLeaderboard?.metrics?.averagePrize ? `${formatNumber(Math.floor(categoryLeaderboard?.metrics?.averagePrize))} bp` : `-`}</div>
                </CardContent>
              </Card>
              <Card className="bg-background">
                <CardHeader className="pb-2 flex flex-col items-center">
                  <div className="rounded-full bg-primary/10 dark:bg-gray-700 w-12 h-12 flex items-center justify-center mb-2">
                    <CreditCard className="w-6 h-6 text-gray-500 dark:text-gray-300" />
                  </div>
                  <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Largest Win
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="text-2xl font-bold text-secondary dark:text-white">{categoryLeaderboard?.metrics?.maximumPrize ? `${formatNumber(categoryLeaderboard?.metrics?.maximumPrize)} bp` : `-`}</div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="flex justify-center space-x-2 mb-8">
            <Button
              variant={timeFrame === "daily" ? "default" : "outline"}
              onClick={() => setTimeFrame("daily")}
              className={`${timeFrame === "daily" ? "dark:text-[#09090B]" : "dark:text-white dark:hover:text-[#09090B]"}`}
            >
              Daily
            </Button>
            <Button
              variant={timeFrame === "weekly" ? "default" : "outline"}
              onClick={() => setTimeFrame("weekly")}
              className={`${timeFrame === "weekly" ? "dark:text-[#09090B]" : "dark:text-white dark:hover:text-[#09090B]"}`}
            >
              Weekly
            </Button>
            <Button
              variant={timeFrame === "monthly" ? "default" : "outline"}
              onClick={() => setTimeFrame("monthly")}
              className={`${timeFrame === "monthly" ? "dark:text-[#09090B]" : "dark:text-white dark:hover:text-[#09090B]"}`}
            >
              Monthly
            </Button>
            <Button
              variant={timeFrame === "all" ? "default" : "outline"}
              onClick={() => setTimeFrame("all")}
              className={`${timeFrame === "all" ? "dark:text-[#09090B]" : "dark:text-white dark:hover:text-[#09090B]"}`}
            >
              All Time
            </Button>
          </div>

          <Card className="bg-background">
            <CardHeader>
              <CardTitle className="text-secondary dark:text-white">{getTimeFrameLabel(timeFrame)} Top Heroes</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20 text-secondary dark:text-gray-400">Rank</TableHead>
                    <TableHead className="text-secondary dark:text-gray-400">User</TableHead>
                    <TableHead className="text-secondary dark:text-gray-400">Total Winnings</TableHead>
                    <TableHead className="text-secondary dark:text-gray-400">Total Polls</TableHead>
                    <TableHead className="text-secondary dark:text-gray-400">Win Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!isCategoryLeaderboardLoading && categoryLeaderboard?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">
                        <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                          <AlertCircle className="w-8 h-8 mb-2" />
                          <p>{getEmptyStateMessage(timeFrame)}</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {categoryLeaderboard?.board?.map((player:LeaderBoard, index:number) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getRankIcon(index + 1)}
                          <span className="font-medium text-secondary dark:text-white">#{index + 1}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-secondary dark:text-white">{player.username}</TableCell>
                      <TableCell className="text-secondary dark:text-white">{player.totalWinnings}</TableCell>
                      <TableCell className="text-secondary dark:text-white">{player.totalPolls}</TableCell>
                      <TableCell className="text-secondary dark:text-white">{player.winRate}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Leaderboard;
